import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import {
  getActFamiliarizationNotificationsBlock,
  getActFamiliarizationNotificationsBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmActFamiliarizationNotificationsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmActFamiliarizationNotificationsForm';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import { ErknmActIntroductionItem } from './ErknmActIntroductionItem';
import styles from './ErknmActIntroduction.module.scss';
import { IRootState } from '../../../../../../CorePackage/MainModule/interfaces/IRootState';
import { getUserRoleIsSupervisor } from '../../../../../../AuthPackage/MainModule/selectors/authSelectors';

interface IErknmActIntroductionsProps {
  id: string;
  name: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isPm?: boolean;
  isDecision?: boolean;
  isDisabledByDirty?: boolean;
}

interface IErknmActIntroductionsStoreProps {
  isSupervisor: boolean;
}

const ErknmActIntroductionsWithoutStore = ({
  id,
  name,
  formRef,
  isDecision,
  isPm,
  isDisabledByDirty = false,
  isSupervisor,
}: IErknmActIntroductionsProps & IErknmActIntroductionsStoreProps) => {
  const remove = formRef?.current.mutators.remove;
  const push = formRef?.current.mutators.push;
  const familiarizationNotificationsBlockId = getActFamiliarizationNotificationsBlock(id);

  const isDisable = isDisabledByDirty || !isSupervisor;

  const addNotify = useCallback(() => {
    if (push) {
      const addingEvent: IKnmActFamiliarizationNotificationsForm = {
        listId: uuidv4(),
        isNew: true,
        knmNoticeType: undefined,
        note: undefined,
        noticeDate: undefined,
      };

      push(`${name}.familiarizationNotifications`, addingEvent);
    }
  }, [formRef, push]);

  const makeRemoveNotifyHandler = (index: number, isNew: boolean) => () =>
    isNew && remove && remove(`${name}.familiarizationNotifications`, index);

  return (
    <FieldBlock
      id={familiarizationNotificationsBlockId}
      className={styles.GroupMarginBottom}
      margin="top"
      label={'Уведомление контролируемого лица об акте'}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          onClick={addNotify}
          disabled={isDisable}
        />
      }
    >
      <>
        <span className={styles.Message}>
          <Hint
            text="Если уведомление контролируемого лица производится через портал Госуслуги, соответствующая информация размещается и
          обновляется автоматически. Вручную указывать такие сведения не требуется."
          />
        </span>
        <FieldArray name={`${name}.familiarizationNotifications`} allowNull>
          {({ fields }: FieldArrayRenderProps<IKnmActFamiliarizationNotificationsForm, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((fieldName, sIndex) => {
              const fieldValue: IKnmActFamiliarizationNotificationsForm = fields.value?.[sIndex];
              const itemId = getActFamiliarizationNotificationsBlockId(id, fieldValue.listId);

              return (
                <ErknmActIntroductionItem
                  fieldValue={fieldValue}
                  id={itemId}
                  key={itemId}
                  isDisabledField={isDisable || !fieldValue.isNew}
                  name={fieldName}
                  remove={makeRemoveNotifyHandler(sIndex, fieldValue.isNew)}
                  isDecision={isDecision}
                  isPm={isPm}
                />
              );
            });
          }}
        </FieldArray>
      </>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmActIntroductionsStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const ErknmActIntroductions = connect(mapStateToProps)(ErknmActIntroductionsWithoutStore);
